/**
 * Cotizaciones — pantalla unificada del módulo.
 *
 * La vista principal es el listado de cotizaciones embebido; la tarjeta
 * "Nueva cotización" abre el formulario a pantalla completa. Una cotización
 * es un documento informativo de precio (contado y/o plan de crédito
 * simulado): no compromete la venta ni reserva el chasis, y por eso aquí no
 * se factura nada — la venta se registra en Facturación como siempre.
 */
import React, { useState } from "react";
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Dialog from "@mui/material/Dialog";
import Typography from "@mui/material/Typography";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import RequestQuoteOutlinedIcon from "@mui/icons-material/RequestQuoteOutlined";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import { useMaterialUIController } from "context";
import { tienePermisosModulo } from "lib/seguridad/permisosPorModulo";
import { FP, PageHeader, ActionCard } from "components/financepro";
import CrearCotizacion from "modules/cotizaciones/crearcotizacion";
import CrearCotizacionXmayor from "modules/cotizaciones/crearcotizacionxmayor";
import ListadoCotizaciones from "modules/cotizaciones/listadocotizaciones";
import ListadoCotizacionesXmayor from "modules/cotizaciones/listadocotizacionesxmayor";
import useAccionInicial from "lib/useAccionInicial";

export default function Cotizaciones() {
  const [crearOpen, setCrearOpen] = useState(false);
  const [crearXmayorOpen, setCrearXmayorOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [tab, setTab] = useState(0);

  const [controller] = useMaterialUIController();
  const tieneAcceso = tienePermisosModulo(controller.roleDetalles, "cotizaciones");

  // Enlace profundo desde el buscador global (?accion=...).
  useAccionInicial({
    nueva: { permitido: tieneAcceso, abrir: () => setCrearOpen(true) },
    nuevaxmayor: { permitido: tieneAcceso, abrir: () => setCrearXmayorOpen(true) },
  });

  if (!tieneAcceso) {
    return (
      <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
        <PageHeader title="Cotizaciones" subtitle="Cotizaciones de motores para clientes" />
        <Box sx={{ p: { xs: 2, md: 3 } }}>
          <Typography sx={{ color: FP.color.textSecondary, fontWeight: 700 }}>
            Su rol no tiene acceso a Cotizaciones.
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box component="main" sx={{ minHeight: "100vh", bgcolor: FP.color.surface }}>
      <PageHeader
        title="Cotizaciones"
        subtitle="Documentos informativos de precio; no comprometen la venta ni reservan el chasis"
      />

      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Grid container spacing={2.5} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={4}>
            <ActionCard
              icon={RequestQuoteOutlinedIcon}
              titulo="Nueva cotización"
              descripcion="Cotiza un chasis del inventario o un modelo libre, al contado o con plan de crédito"
              color={FP.color.navy}
              onClick={() => setCrearOpen(true)}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <ActionCard
              icon={LocalShippingOutlinedIcon}
              titulo="Cotización x mayor"
              descripcion="Varios chasis o líneas libres para un cliente mayorista, con plan de cuotas simulado"
              color={FP.color.accentBlue}
              onClick={() => setCrearXmayorOpen(true)}
            />
          </Grid>
        </Grid>

        {/* Vista principal: listados por tipo */}
        <Tabs
          value={tab}
          onChange={(_e, v) => setTab(v)}
          sx={{
            mb: 2,
            "& .MuiTab-root": { textTransform: "none", fontWeight: 700 },
          }}
        >
          <Tab label="Al detalle" />
          <Tab label="X mayor" />
        </Tabs>
        {tab === 0 ? (
          <ListadoCotizaciones embedded refreshToken={refreshKey} />
        ) : (
          <ListadoCotizacionesXmayor embedded refreshToken={refreshKey} />
        )}
      </Box>

      <Dialog fullScreen open={crearOpen} onClose={() => setCrearOpen(false)}>
        <CrearCotizacion
          embedded
          onExit={() => setCrearOpen(false)}
          onSaved={() => {
            setRefreshKey((k) => k + 1);
            setTab(0);
          }}
        />
      </Dialog>

      <Dialog fullScreen open={crearXmayorOpen} onClose={() => setCrearXmayorOpen(false)}>
        <CrearCotizacionXmayor
          embedded
          onExit={() => setCrearXmayorOpen(false)}
          onSaved={() => {
            setRefreshKey((k) => k + 1);
            setTab(1);
          }}
        />
      </Dialog>
    </Box>
  );
}
